import { useEffect, useState } from "react";
import useSWR from "swr";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Card, btn, inputCls } from "@/components/common/Ornament";
import { RichEditor } from "@/components/common/RichText";

const Lbl = ({ children }) => <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-[#3E5C4E] mb-1.5">{children}</label>;

export function ListEditor({ items, onChange, fields, testId, blank }) {
  const set = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-2" data-testid={testId}>
      {items.map((it, i) => (
        <div key={i} className="flex flex-wrap gap-2 items-center">
          {fields.map(([k, ph, w, type, options]) => (type === "select" ? (
            <select key={k} value={it[k] ?? ""} onChange={(e) => set(i, k, e.target.value)} className={inputCls + " " + w} data-testid={`${testId}-${k}-${i}`}>
              {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ) : (
            <input key={k} value={it[k] ?? ""} placeholder={ph} type={type || "text"} step="any"
              onChange={(e) => set(i, k, type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
              className={inputCls + " " + w} data-testid={`${testId}-${k}-${i}`} />
          )))}
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} className={btn.danger} data-testid={`${testId}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button onClick={() => onChange([...items, blank])} className={btn.ghost + " !h-9"} data-testid={`${testId}-add`}><Plus className="h-4 w-4" /> Add</button>
    </div>
  );
}

export default function ContentTab() {
  const { data, mutate } = useSWR("/content");
  const { data: photos = [] } = useSWR("/photos");
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data && !f) setF(JSON.parse(JSON.stringify(data)));
  }, [data, f]);
  if (!f) return <Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" />;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setMap = (k, v) => setF((s) => ({ ...s, map: { ...s.map, [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      const { disclaimer, cost_detail, ...rest } = f;
      rest.map = { ...rest.map, attractions: rest.map.attractions.filter((a) => a.name && a.lat !== "" && a.lng !== "").map((a) => ({ ...a, lat: Number(a.lat), lng: Number(a.lng) })) };
      const { data: d } = await api.put("/admin/content", rest);
      mutate(d, false);
      toast.success("Content saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card eyebrow="Overview Page" title="Headline & Key Facts" testId="admin-content-headline">
        <div className="grid md:grid-cols-2 gap-4">
          <div><Lbl>Project name</Lbl><input value={f.project_name} onChange={(e) => set("project_name", e.target.value)} className={inputCls} data-testid="admin-content-project-name" /></div>
          <div><Lbl>Tagline</Lbl><input value={f.tagline} onChange={(e) => set("tagline", e.target.value)} className={inputCls} data-testid="admin-content-tagline" /></div>
          <div><Lbl>Property address</Lbl><input value={f.address} onChange={(e) => set("address", e.target.value)} className={inputCls} data-testid="admin-content-address" /></div>
          <div><Lbl>Target opening date</Lbl><input value={f.target_opening} onChange={(e) => set("target_opening", e.target.value)} className={inputCls} data-testid="admin-content-opening" /></div>
          <div className="md:col-span-2"><Lbl>Hero image</Lbl>
            <select value={f.hero_photo_id || ""} onChange={(e) => set("hero_photo_id", e.target.value || null)} className={inputCls} data-testid="admin-content-hero-select">
              <option value="">None</option>
              {photos.map((p) => <option key={p.id} value={p.id}>{p.caption}</option>)}
            </select>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-3">Room count, total project cost and cost per key are calculated from the Baseline Financials tab.</p>
        <div className="mt-5"><Lbl>Highlight figures</Lbl>
          <ListEditor items={f.highlights} onChange={(v) => set("highlights", v)} fields={[["label", "Label", "sm:w-56"], ["value", "Value", "sm:w-56"]]} blank={{ label: "", value: "" }} testId="admin-highlights" />
        </div>
      </Card>
      <Card eyebrow="Overview Page" title="Executive Summary & Status" testId="admin-content-text">
        <Lbl>Executive summary</Lbl>
        <RichEditor value={f.exec_summary} onChange={(v) => set("exec_summary", v)} testId="admin-editor-exec" />
        <div className="mt-5"><Lbl>Development status</Lbl><RichEditor value={f.status_html} onChange={(v) => set("status_html", v)} testId="admin-editor-status" /></div>
      </Card>
      <Card eyebrow="Overview Page" title="Project Timeline" testId="admin-content-timeline">
        <p className="text-xs text-slate-500 mb-3">Items with the same start and end date (or within 10 days) show as milestones.</p>
        <ListEditor items={f.timeline || []} onChange={(v) => set("timeline", v)} blank={{ label: "", phase: "design", start: "", end: "" }} testId="admin-timeline"
          fields={[["label", "Activity", "sm:w-80"], ["phase", "", "sm:w-56", "select", [["design", "Design"], ["permit", "Permit / Bidding / Contract"], ["construction", "Construction"]]], ["start", "Start", "w-40", "date"], ["end", "End", "w-40", "date"]]} />
      </Card>
      <Card eyebrow="Market Benchmarks Page" title="Benchmarks Write-Up" testId="admin-content-benchmarks">
        <RichEditor value={f.benchmarks_html} onChange={(v) => set("benchmarks_html", v)} testId="admin-editor-benchmarks" />
      </Card>
      <Card eyebrow="Property Page" title="Description, Media & Map" testId="admin-content-property">
        <Lbl>About the hotel</Lbl>
        <RichEditor value={f.property_html} onChange={(v) => set("property_html", v)} testId="admin-editor-property" />
        <div className="grid md:grid-cols-2 gap-4 mt-5">
          <div><Lbl>Video URL (YouTube / Vimeo)</Lbl><input value={f.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://…" className={inputCls} data-testid="admin-content-video-url" /></div>
          <div><Lbl>3D / virtual tour URL</Lbl><input value={f.tour_url} onChange={(e) => set("tour_url", e.target.value)} placeholder="https://my.matterport.com/show/?m=…" className={inputCls} data-testid="admin-content-tour-url" /></div>
        </div>
        <div className="mt-5"><Lbl>Subject property map pin</Lbl>
          <div className="flex flex-wrap gap-2">
            <input value={f.map.subject.label} onChange={(e) => setMap("subject", { ...f.map.subject, label: e.target.value })} className={inputCls + " sm:w-72"} data-testid="admin-map-subject-label" />
            <input type="number" step="any" value={f.map.subject.lat} onChange={(e) => setMap("subject", { ...f.map.subject, lat: Number(e.target.value) })} className={inputCls + " w-36"} data-testid="admin-map-subject-lat" />
            <input type="number" step="any" value={f.map.subject.lng} onChange={(e) => setMap("subject", { ...f.map.subject, lng: Number(e.target.value) })} className={inputCls + " w-36"} data-testid="admin-map-subject-lng" />
          </div>
        </div>
        <div className="mt-5"><Lbl>Nearby attractions / demand drivers</Lbl>
          <ListEditor items={f.map.attractions} onChange={(v) => setMap("attractions", v)} blank={{ name: "", lat: "", lng: "", description: "" }} testId="admin-attractions"
            fields={[["name", "Name", "sm:w-60"], ["lat", "Latitude", "w-32", "number"], ["lng", "Longitude", "w-32", "number"], ["description", "Description", "sm:w-64"]]} />
        </div>
      </Card>
      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={busy} className={btn.gold + " shadow-lg"} data-testid="admin-content-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save content</button>
      </div>
    </div>
  );
}
